import { ReactNode } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { X } from 'lucide-react';

interface DrawerShellProps {
  title: string;
  onClose: () => void;
  onSubmit: () => void;
  submitting: boolean;
  submitLabel: string;
  submittingLabel: string;
  children: ReactNode;
}

/**
 * Каркас выезжающей справа панели (overlay + панель + шапка + контент + футер).
 * Переиспользуется формами пользователей и ролей.
 */
export default function DrawerShell({
  title,
  onClose,
  onSubmit,
  submitting,
  submitLabel,
  submittingLabel,
  children,
}: DrawerShellProps) {
  return (
    <>
      <Box
        onClick={onClose}
        sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(13,17,23,0.32)', zIndex: 299 }}
      />
      <Box
        sx={{
          position: 'fixed',
          right: 0,
          top: 0,
          height: '100vh',
          width: 440,
          maxWidth: '92vw',
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(28px)',
          WebkitBackdropFilter: 'blur(28px)',
          borderLeft: '1px solid rgba(255,255,255,0.72)',
          boxShadow: '0 0 40px rgba(20,116,255,0.14), -1px 0 0 rgba(20,116,255,0.10)',
          zIndex: 300,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid var(--color-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--color-text-primary)' }}>
            {title}
          </Typography>
          <Box
            onClick={onClose}
            sx={{
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              color: 'var(--color-text-muted)',
              '&:hover': { bgcolor: 'var(--color-bg)', color: 'var(--color-text-primary)' },
            }}
          >
            <X size={16} />
          </Box>
        </Box>

        {/* Scrollable content */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>{children}</Box>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={submitting}
            fullWidth
            startIcon={
              submitting ? <CircularProgress size={13} sx={{ color: 'inherit' }} /> : undefined
            }
            sx={{
              bgcolor: 'var(--color-primary)',
              fontSize: 13,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#4a4de0', boxShadow: 'none' },
            }}
          >
            {submitting ? submittingLabel : submitLabel}
          </Button>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={submitting}
            fullWidth
            sx={{
              fontSize: 13,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 'var(--radius-md)',
              borderColor: 'var(--color-border)',
              color: 'var(--color-text-secondary)',
              '&:hover': { borderColor: 'var(--color-border-hover)', bgcolor: 'var(--color-bg)' },
            }}
          >
            Отмена
          </Button>
        </Box>
      </Box>
    </>
  );
}

/** Блок с сообщением об ошибке API внутри drawer'а. */
export function DrawerError({ message }: { message: string }) {
  if (!message) return null;
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        mb: 2,
        borderRadius: 'var(--radius-md)',
        bgcolor: 'rgba(220,38,38,0.06)',
        border: '1px solid rgba(220,38,38,0.18)',
      }}
    >
      <Typography sx={{ fontSize: 12, color: '#dc2626' }}>{message}</Typography>
    </Box>
  );
}
